import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppSettings } from '../../app.settings';
import { ApiService } from '../api/api.service';
import { ManagedFeatureFlagsService } from '../managed-feature-flags/managed-feature-flags.service';
import { TEAM_ADMIN_NATIVE_TEAM_WORKSPACE_FLAG } from '../managed-feature-flags/managed-feature-flag-keys';
import { SessionService } from '../session/session.service';
import { TeamCompanyUsersService } from '../teamcompanyusers/teamcompanyusers.service';
import { TeamsService } from '../teams/teams.service';
import { UsersService } from '../users/users.service';
import { PayrollAccessService } from '../payroll-access/payroll-access.service';

export interface TeamManagerWorkspaceState {
  loading: boolean;
  enabled: boolean;
  teams: any[];
}

/** Presentation eligibility only. Team reads and actions retain their existing backend authorization. */
@Injectable({ providedIn: 'root' })
export class TeamManagerWorkspaceService {
  readonly stateChanges = new BehaviorSubject<TeamManagerWorkspaceState>({
    loading: false, enabled: false, teams: [],
  });
  private version = 0;
  private userId = '';
  private request?: { userId: string; promise: Promise<TeamManagerWorkspaceState> };

  constructor(
    private apiService: ApiService,
    private sessionService: SessionService,
    private usersService: UsersService,
    private teamCompanyUsersService: TeamCompanyUsersService,
    private teamsService: TeamsService,
    private flags: ManagedFeatureFlagsService,
    private payrollAccessService: PayrollAccessService,
  ) {}

  get state(): TeamManagerWorkspaceState { return this.stateChanges.value; }

  // Opening the shared page does not opt a superadmin into team-manager navigation.
  get useNativeNavigation(): boolean {
    return this.state.enabled && !this.sessionService.isSuperAdmin();
  }

  get isSuperadminWorkspace(): boolean {
    return this.state.enabled && this.userId === this.sessionService.getUserId()
      && this.apiService.isAuthenticated() && this.sessionService.isExactSuperAdmin();
  }

  clear(): void {
    this.version++;
    this.userId = '';
    this.request = undefined;
    this.stateChanges.next({ loading: false, enabled: false, teams: [] });
  }

  refresh(): Promise<TeamManagerWorkspaceState> {
    const userId = this.sessionService.getUserId();
    if (this.request?.userId === userId) return this.request.promise;
    const promise = this.refreshForCurrentUser();
    this.request = { userId, promise };
    void promise.finally(() => {
      if (this.request?.promise === promise) this.request = undefined;
    });
    return promise;
  }

  private async refreshForCurrentUser(): Promise<TeamManagerWorkspaceState> {
    const version = ++this.version;
    const userId = this.sessionService.getUserId();
    const previous = this.userId === userId && this.apiService.isAuthenticated() ? this.state : null;
    this.userId = userId;
    // Keep the same user's shell stable while the route guard awaits fresh eligibility.
    this.stateChanges.next({ loading: true, enabled: previous?.enabled || false, teams: previous?.teams || [] });
    const isCurrent = () => version === this.version
      && userId === this.sessionService.getUserId() && this.apiService.isAuthenticated();
    const publish = (enabled = false, teams: any[] = []) => {
      if (isCurrent()) this.stateChanges.next({ loading: false, enabled, teams });
      return this.state;
    };
    if (!userId || !this.apiService.isAuthenticated()) {
      this.clear();
      return this.state;
    }
    if (this.sessionService.isTeamAgentSession()
      || AppSettings.isPayrollAdmin() || AppSettings.isBroker() || AppSettings.isCustomerSupport()
      || AppSettings.isContentAdmin() || AppSettings.isIntegration() || AppSettings.isFinance()) {
      return publish();
    }
    try {
      if (this.sessionService.isExactSuperAdmin()) {
        const access = await this.payrollAccessService.refresh();
        return publish(access.canShowPayrollAdminNav);
      }
      if (this.sessionService.isSuperAdmin()) return publish();
      const response = await this.teamCompanyUsersService.getManagers();
      const roles = response?.plain?.() || response || [];
      // Company managers, Payroll Admins, and Team Agents retain their established workspace.
      if (!Array.isArray(roles) || roles.some(role => ['manager', 'payrolladmin', 'teamagent'].includes(role?.role))) {
        return publish();
      }
      const me = this.usersService.me();
      const enabled = await this.flags.isEnabled(TEAM_ADMIN_NATIVE_TEAM_WORKSPACE_FLAG, 'team-admin', {
        userId,
        partnerId: me?.user?.partnerId || me?.brand?.partnerId || undefined,
      });
      if (!isCurrent()) return this.state;
      if (!enabled) return publish();
      // This existing endpoint lists owned teams and delegated team-admin memberships, not company teams.
      const responseTeams = await this.teamsService.getUserTeamsInfo(userId);
      const teams = Array.isArray(responseTeams) ? responseTeams.filter(team => !!team?.id) : [];
      return publish(teams.length > 0, teams);
    } catch (_) {
      return publish();
    }
  }

  managesTeam(teamId: string): boolean {
    return this.state.enabled && this.userId === this.sessionService.getUserId()
      && !!teamId && (this.isSuperadminWorkspace
        ? this.teamsService.wellspaceChangedObservable.value?.id === teamId
        : this.state.teams.some(team => team.id === teamId));
  }

  async ensureSelectedTeam(): Promise<any | null> {
    const candidateId = this.teamsService.getSelectedWellspaceCandidateId();
    if (this.isSuperadminWorkspace) {
      // Re-read the selected team through the existing authorized API, including on direct entry.
      return candidateId ? this.selectTeam(candidateId) : null;
    }
    if (candidateId && this.managesTeam(candidateId)) {
      const team = await this.teamsService.ensureValidatedSelectedWellspace();
      return team && this.managesTeam(team.id) ? team : null;
    }
    if (candidateId) this.teamsService.clearSelectedWellspace();
    return this.state.teams.length === 1 ? this.selectTeam(this.state.teams[0].id) : null;
  }

  async selectTeam(teamId: string): Promise<any | null> {
    if (!teamId || (!this.isSuperadminWorkspace && !this.managesTeam(teamId))) return null;
    const userId = this.sessionService.getUserId();
    const version = this.version;
    const team = await this.teamsService.setWellspace({ id: teamId });
    return userId === this.sessionService.getUserId() && version === this.version
      && team?.id === teamId && this.managesTeam(team.id) ? team : null;
  }
}
